import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from "react";
import { useStorage } from "@/hooks/useStorage";
import type { CallRequestFormData } from "@/types/contexts/callRequest";

const STORAGE_KEYS = {
  FORM_DATA: "@call_request_form_data",
  LAST_STEP: "@call_request_last_step",
} as const;

const initialFormData: CallRequestFormData = {
  companyName: "",
  submitterEmail: "",
  jobStartDate: "",
  jobEndDate: "",
  jobName: "",
  venueName: "",
  venueAddress: "",
  mealOption: "",
  indoorOutdoor: "",
  multipleLocations: "",
  preferredUniform: "",
  onsiteContactName: "",
  onsiteContactPhone: "",
  requestSpecificMembers: "",
  specificMemberRequests: "",
  calls: [
    {
      callName: "",
      date: "",
      startTime: "",
      estimatedDuration: "",
      positions: [{ positionName: "", quantity: "" }],
    },
  ],
};

type CallRequestContextType = {
  formData: CallRequestFormData;
  updateFormData: (updates: Partial<CallRequestFormData>) => void;
  resetFormData: () => void;
  currentStep: number;
  updateStep: (step: number) => void;
  clearSavedData: () => void;
  hasSavedData: boolean;
  isLoading: boolean;
};

const CallRequestContext = createContext<CallRequestContextType | undefined>(undefined);

export function CallRequestProvider({ children }: { children: ReactNode }) {
  const storage = useStorage();
  const [formData, setFormData] = useState<CallRequestFormData>(initialFormData);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSavedData, setHasSavedData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load saved data on mount
  useEffect(() => {
    try {
      const savedFormData = storage.getString(STORAGE_KEYS.FORM_DATA);
      const savedStep = storage.getNumber(STORAGE_KEYS.LAST_STEP);

      if (savedFormData) {
        const parsed = JSON.parse(savedFormData) as CallRequestFormData;
        setFormData(parsed);
        setHasSavedData(true);
      }

      if (savedStep !== undefined) {
        setCurrentStep(savedStep);
      }
    } catch (error) {
      console.error("Error loading saved call request data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [storage]);

  // Debounced save to storage
  const saveToStorage = useCallback(
    (data: CallRequestFormData, step: number) => {
      // Only save once user has progressed past step 0
      if (step === 0) {
        return;
      }

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        try {
          storage.saveFast(STORAGE_KEYS.FORM_DATA, JSON.stringify(data));
          storage.saveFast(STORAGE_KEYS.LAST_STEP, step);
          setHasSavedData(true);
        } catch (error) {
          console.error("Error saving call request data:", error);
        }
      }, 500);
    },
    [storage],
  );

  const updateFormData = useCallback(
    (updates: Partial<CallRequestFormData>) => {
      setFormData((prev) => {
        const next = { ...prev, ...updates };
        saveToStorage(next, currentStep);
        return next;
      });
    },
    [currentStep, saveToStorage],
  );

  const updateStep = useCallback(
    (step: number) => {
      setCurrentStep(step);
      saveToStorage(formData, step);
    },
    [formData, saveToStorage],
  );

  const resetFormData = useCallback(() => {
    setFormData(initialFormData);
    setCurrentStep(0);
  }, []);

  const clearSavedData = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    storage.removeItem(STORAGE_KEYS.FORM_DATA);
    storage.removeItem(STORAGE_KEYS.LAST_STEP);
    setFormData(initialFormData);
    setCurrentStep(0);
    setHasSavedData(false);
  }, [storage]);

  return (
    <CallRequestContext.Provider
      value={{
        formData,
        updateFormData,
        resetFormData,
        currentStep,
        updateStep,
        clearSavedData,
        hasSavedData,
        isLoading,
      }}
    >
      {children}
    </CallRequestContext.Provider>
  );
}

export function useCallRequest() {
  const context = useContext(CallRequestContext);
  if (context === undefined) {
    throw new Error("useCallRequest must be used within a CallRequestProvider");
  }
  return context;
}
